// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { AuthnMethod } from '@imprivata-cloud/authn';
import type { TraceContext } from '@imprivata-cloud/common/types';
import { Observable, share } from 'rxjs';
import type { OidcRequestData } from '@imprivata-cloud/authn/lib/types/api';
import {
  addSpanEvent,
  createSpanName,
  instrument,
  SpanComponent,
} from '@imprivata-cloud/common';
import type {
  AppAuthenticationCanceledEventHandler,
  AppAuthenticationStatusEventHandler,
  AuthenticationStatusCode,
  CloseWindowEventHandler,
  FactorChangedEventHandler,
  FactorDataReadyEventHandler,
  InternetConnectivityStatusHandler,
  StatusData,
} from '../types';
import {
  type ComponentData,
  ComponentId,
  type IdentityTokenData,
  type SessionData,
  type SessionDataKey,
  ShowWindowOption,
  type SystemData,
  type SystemDataProperty,
  type WindowBounds,
  WindowPositionFlag,
  type WindowStyle,
} from './types';
import {
  getWindowStyle,
  isDesktopAccess,
  isForeground,
  normalizeFactors,
} from '../utils/utils';
import { AgentEvent } from '../constants';
import { log } from '../utils/logging';
import { SpanNames } from '../tracing/constants';
import { getWorkflowId } from '../tracing';
import { getTraceContext } from '../tracing/traceContext';

const serviceSpanName = createSpanName(SpanComponent.UI, 'APP-ACCESS', 'Agent');

export type FactorFilter = { factorType: AuthnMethod[] } | null;

export function subscribeToAuthnDataReady(
  callback: FactorDataReadyEventHandler,
) {
  subscribeToAgentEvent(
    AgentEvent.FACTOR_DATA_READY,
    { factorType: [AuthnMethod.PROX] },
    instrument(
      createSpanName(SpanComponent.UI, SpanNames.FACTOR_DATA_READY),
      (eventName: AgentEvent, data: object) => {
        if (isForeground()) {
          log('handled event', AgentEvent.FACTOR_DATA_READY);
          // @ts-expect-error - ...
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          callback(eventName, data);
        } else {
          log('skipped event', AgentEvent.FACTOR_DATA_READY);
        }
      },
    ),
  );
}

export function subscribeToFactorChanged(callback: FactorChangedEventHandler) {
  subscribeToAgentEvent(
    AgentEvent.FACTOR_CHANGED,
    { factorType: [AuthnMethod.PROX] },
    (...[eventName, data]: Parameters<FactorChangedEventHandler>) => {
      const factorList = normalizeFactors(data.factorList);
      log(
        'subscribeToFactorChanged',
        JSON.stringify({ eventName, data }),
        factorList,
      );
      callback(eventName, { ...data, factorList });
    },
  );
}

export function subscribeToAppAuthenticationStatus(
  callback: AppAuthenticationStatusEventHandler,
) {
  subscribeToAgentEvent(
    AgentEvent.APP_AUTHENTICATION_STATUS,
    null,
    // eslint-disable-next-line
    // @ts-ignore
    (...args) => {
      log('handled event', AgentEvent.APP_AUTHENTICATION_STATUS, args);
      // @ts-expect-error - ...
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      callback(...args);
    },
  );
}

export function subscribeToCloseWindow(callback: CloseWindowEventHandler) {
  subscribeToAgentEvent(AgentEvent.CLOSE_WINDOW, null, callback);
}

export function subscribeToInternetConnectivityStatus(
  callback: InternetConnectivityStatusHandler,
) {
  subscribeToAgentEvent(
    AgentEvent.NETWORK_CONNECTIVITY_CHANGED,
    null,
    // eslint-disable-next-line
    // @ts-ignore
    (...args) => {
      log(`handled event`, AgentEvent.NETWORK_CONNECTIVITY_CHANGED, args);
      // @ts-expect-error - ...
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      callback(...args);
    },
  );
}

export async function getAppAccessContext(): Promise<string> {
  return new Promise<string>(
    instrument(
      createSpanName(SpanComponent.UI, SpanNames.GET_APP_ACCESS_CONTEXT),
      resolve => {
        log('call getAppAccessContext');
        const traceContext = getTraceContext();
        window.impr?.authn.getAppAccessContext(
          traceContext.workflowId,
          traceContext,
          (appAccessContext: string) => {
            log('execute callback for getAppAccessContext');
            resolve(appAccessContext);
          },
        );
      },
    ),
  );
}

/** Endpoint UUID the agent registered with; '' in a browser or before the first registration. */
export const getEndpointId = (): string => window.impr?.agent?.endpointId ?? '';

export const getSystemDataProperty = (property: SystemDataProperty): string => {
  if (!window.impr?.agent.systemData) {
    return '';
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
  return window.impr && JSON.parse(window.impr?.agent.systemData)[property];
};

/**
 * Reads complete system data from the Endpoint Agent if available.
 * Returns an object containing computerName, osName, and osVersion.
 * These values are needed for the login activity report.
 *
 * @returns System data object or undefined if not available
 */
export const getSystemDataFromAgent = (): SystemData | undefined => {
  try {
    if (!window.impr?.agent?.systemData) {
      return undefined;
    }
    const parsed = JSON.parse(
      window.impr.agent.systemData,
    ) as Partial<SystemData>;
    return {
      computerName:
        typeof parsed.computerName === 'string' ? parsed.computerName : '',
      osName: typeof parsed.osName === 'string' ? parsed.osName : '',
      osVersion: typeof parsed.osVersion === 'string' ? parsed.osVersion : '',
    };
  } catch (error) {
    log('[AppAccess] Failed to read systemData from agent:', String(error));
    return undefined;
  }
};

export const authenticationStatus$ = new Observable<[TraceContext, StatusData]>(
  observer => {
    const callback: AppAuthenticationStatusEventHandler = (_, data) => {
      observer.next([data.traceContext, data.statusData]);
    };
    setWindowStyle(getWindowStyle());
    subscribeToAgentEvent(AgentEvent.APP_AUTHENTICATION_STATUS, null, callback);
  },
).pipe(share());

export const endJourneyAndCloseWindow = instrument(
  createSpanName(SpanComponent.UI, SpanNames.END_JOURNEY_AND_CLOSE_WINDOW),
  (journeyId: string, traceContext: TraceContext) => {
    log('call endJourneyAndCloseWindow');
    window.impr?.authn.endJourneyAndCloseWindow(journeyId, traceContext);
  },
);

/**
 * Sends authentication-related data back to the Endpoint Agent.
 *
 * Important note: do NOT change the order of passing the OIDC auth code and the credentials. The
 * Endpoint Agent relies on the calling order of these Web APIs.
 *
 * @param authnData contains credentials and/or SAML assertion
 * @param oidcAuthCode contains the OIDC authorization code (optional)
 */
export const sendAuthnDataToAgent = instrument(
  createSpanName(SpanComponent.UI, SpanNames.SUBMIT_CREDENTIALS),
  (authnData: CredentialData, oidcAuthCode?: string) => {
    if (oidcAuthCode) {
      passOidcAuthCodeToAgent(oidcAuthCode);
    }
    const traceContext = getTraceContext();
    log('call submitCredentials2');
    window.impr?.authn.submitCredentials2(
      traceContext.workflowId,
      traceContext,
      authnData,
    );
  },
);

const passOidcAuthCodeToAgent = instrument(
  createSpanName(SpanComponent.UI, SpanNames.SET_OIDC_AUTH_CODE),
  (oidcAuthCode: string): void => {
    addSpanEvent(createSpanName(serviceSpanName, SpanNames.SET_OIDC_AUTH_CODE));
    const traceContext = getTraceContext();
    log('call setAuthZCode');
    window.impr?.authn.setAuthZCode(
      traceContext.workflowId,
      traceContext,
      oidcAuthCode,
    );
  },
);

export async function getFactorTypes(): Promise<AuthnMethod[]> {
  return new Promise<AuthnMethod[]>(
    instrument(
      createSpanName(SpanComponent.UI, SpanNames.GET_DEVICE_FACTORS),
      resolve => {
        log('pass callback to getDeviceFactorTypes');
        const traceContext = getTraceContext();
        window.impr?.authn.getDeviceFactorTypes(
          traceContext,
          (agentFactors: AuthnMethod[]) => {
            log('execute callback for getDeviceFactorTypes', agentFactors);
            resolve(agentFactors);
          },
        );
      },
    ),
  );
}

export const setAuthenticationStatus = instrument(
  createSpanName(SpanComponent.UI, SpanNames.SET_AUTHENTICATION_STATUS),
  (statusCode: AuthenticationStatusCode) => {
    const traceContext = getTraceContext();
    log('call setAuthenticationStatus', statusCode);
    window.impr?.authn.setAuthenticationStatus(
      traceContext.workflowId,
      traceContext,
      statusCode,
    );
  },
);

const SET_WINDOW_POSITION_AFTER_VERSION = '26.09.46496';
const COMPONENT_INFORMATION_TIMEOUT_MS = 2000;
// Temporary fixed size; the full-width bounds below are commented out until it is confirmed.
// 1120 = 1920 − 2 × 400: centered on a 1920 px screen, it leaves 400 px on the right
// for the Windows power flyout ("Restart anyway"), anchored bottom-right.
const LOCK_SCREEN_WINDOW_CSS_PX = { width: 1120, height: 820 };
// Worst case across Windows 10/11 and DPI: 48px button + 8px margin + 32px clearance.
// const LOCK_SCREEN_BUTTONS_CSS_PX = 88;
const VERSION_PATTERN = /^\d+(\.\d+)*$/;

export const isVersionAfter = (
  version: string | undefined,
  baseline: string,
): boolean => {
  if (!version || !VERSION_PATTERN.test(version)) {
    return false;
  }
  const segments = version.split('.').map(Number);
  const baselineSegments = baseline.split('.').map(Number);
  const firstDifference = Array.from(
    { length: Math.max(segments.length, baselineSegments.length) },
    (_, i) => (segments[i] ?? 0) - (baselineSegments[i] ?? 0),
  ).find(difference => difference !== 0);
  return firstDifference !== undefined && firstDifference > 0;
};

/**
 * Lock-screen window bounds, in physical pixels: 1120×820 CSS px, shrunk to
 * fit a smaller screen so no control lands off-screen, and centered.
 */
export const lockScreenBounds = (
  screen: Pick<Screen, 'width' | 'height'>,
  devicePixelRatio: number,
): WindowBounds => {
  const width = Math.min(LOCK_SCREEN_WINDOW_CSS_PX.width, screen.width);
  const height = Math.min(LOCK_SCREEN_WINDOW_CSS_PX.height, screen.height);
  const toPhysical = (cssPx: number) => Math.round(cssPx * devicePixelRatio);
  const centeredOffset = (screenLength: number, windowLength: number) =>
    toPhysical((screenLength - windowLength) / 2);
  return {
    x: centeredOffset(screen.width, width),
    y: centeredOffset(screen.height, height),
    width: toPhysical(width),
    height: toPhysical(height),
  };
};

// Full-width window bounds, in physical pixels, that stop above the Windows
// lock-screen buttons in the bottom-right corner.
// export const lockScreenBounds = (
//   screen: Pick<Screen, 'width' | 'height'>,
//   devicePixelRatio: number,
// ): WindowBounds | undefined => {
//   const height = Math.round(
//     (screen.height - LOCK_SCREEN_BUTTONS_CSS_PX) * devicePixelRatio,
//   );
//   return height > 0
//     ? { x: 0, y: 0, width: Math.round(screen.width * devicePixelRatio), height }
//     : undefined;
// };

const resolveAfter = async <T>(ms: number, value: T) =>
  new Promise<T>(resolve => {
    setTimeout(() => {
      resolve(value);
    }, ms);
  });

let setWindowPositionSupport: Promise<boolean> | undefined;

// Agent version cannot change within a session, so the check runs once.
const supportsSetWindowPosition = async (): Promise<boolean> =>
  (setWindowPositionSupport ??= Promise.race([
    getComponentData(ComponentId.ENDPOINT_AGENT).then(
      data => data?.version,
      (error: unknown) => {
        log('getComponentInformation failed', String(error));
        return undefined;
      },
    ),
    resolveAfter(COMPONENT_INFORMATION_TIMEOUT_MS, undefined),
  ]).then(version => {
    const supported = isVersionAfter(
      version,
      SET_WINDOW_POSITION_AFTER_VERSION,
    );
    log('setWindowPosition supported:', supported, ', agent version:', version);
    return supported;
  }));

const applyShowWindow = instrument(
  createSpanName(SpanComponent.UI, SpanNames.SHOW_WINDOW),
  (showWindowOption: ShowWindowOption, sizeToLockScreen: boolean) => {
    const traceContext = getTraceContext();
    const bounds = sizeToLockScreen
      ? lockScreenBounds(window.screen, window.devicePixelRatio)
      : undefined;
    if (bounds) {
      log('call setWindowPosition', JSON.stringify(bounds));
      window.impr?.window.setWindowPosition(
        traceContext.workflowId,
        traceContext,
        bounds.x,
        bounds.y,
        bounds.width,
        bounds.height,
        [WindowPositionFlag.show],
      );
    } else {
      log('call showWindow2', showWindowOption);
      window.impr?.window.showWindow2(traceContext.workflowId, traceContext, [
        showWindowOption,
      ]);
    }
  },
);

let windowCommands: Promise<void> = Promise.resolve();

/**
 * Shows or hides the window. On Desktop Access with an agent that supports
 * `setWindowPosition`, showing also sizes the window to the lock screen; other
 * flows run on an unlocked desktop and keep the agent's own size. Window
 * commands run one after another in call order; only a Desktop Access `show`
 * waits for the capability check.
 * Await the result before navigating away, or the command may never reach
 * the agent.
 * @param showWindowOption - The option to show or hide the window.
 */
export const showWindow = async (
  showWindowOption: ShowWindowOption,
): Promise<void> => {
  windowCommands = windowCommands
    .then(async () => {
      const sizeToLockScreen =
        showWindowOption === ShowWindowOption.show &&
        isDesktopAccess() &&
        (await supportsSetWindowPosition());
      applyShowWindow(showWindowOption, sizeToLockScreen);
    })
    .catch((error: unknown) => {
      log('showWindow failed', showWindowOption, String(error));
    });
  return windowCommands;
};

/**
 * Sets the window style.
 * @param windowStyle - An array of window styles to set.
 */
export const setWindowStyle = instrument(
  createSpanName(SpanComponent.UI, SpanNames.SET_WINDOW_STYLE),
  (windowStyle: WindowStyle[]): void => {
    const traceContext = getTraceContext();
    log('call setWindowStyle', windowStyle);
    window.impr?.window.setWindowStyle(
      traceContext.workflowId,
      traceContext,
      windowStyle,
    );
  },
);

type SubscriptionArguments =
  | [
      AgentEvent.APP_AUTHENTICATION_STATUS,
      FactorFilter,
      AppAuthenticationStatusEventHandler,
    ]
  | [
      AgentEvent.APP_AUTHENTICATION_CANCELED,
      FactorFilter,
      AppAuthenticationCanceledEventHandler,
    ]
  | [AgentEvent.FACTOR_DATA_READY, FactorFilter, FactorDataReadyEventHandler]
  | [AgentEvent.CLOSE_WINDOW, FactorFilter, CloseWindowEventHandler]
  | [AgentEvent.FACTOR_CHANGED, FactorFilter, FactorChangedEventHandler]
  | [
      AgentEvent.NETWORK_CONNECTIVITY_CHANGED,
      null,
      InternetConnectivityStatusHandler,
    ];

export function subscribeToAgentEvent(
  ...[event, filter, callback]: SubscriptionArguments
): void {
  log('subscribe to event', event);
  window.impr?.event.on2(
    [event],
    getWorkflowId(),
    filter,
    // eslint-disable-next-line
    // @ts-ignore
    (eventName: AgentEvent, data: object) => {
      log(
        'execute callback for event',
        'event:',
        eventName,
        ', data:',
        JSON.stringify(data),
      );
      // WorkflowId is not part of Endpoint Agent TraceContext. Some of the auth functions require it.
      // Because of this explicitly adds workflowId to the traceContext
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      // @ts-expect-error - ...
      callback(eventName, {
        ...data,
        traceContext: {
          // @ts-expect-error - ...
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
          spanContext: data?.traceContext.spanContext,
          workflowId: getTraceContext().workflowId,
        },
      });
    },
  );
}

/**
 * Retrieves PKCE data from the Endpoint Agent.
 * @returns Promise<OidcRequestData> - the Oidc Request Data
 */
export async function getPKCEData(): Promise<OidcRequestData> {
  return new Promise<OidcRequestData>(
    instrument(
      createSpanName(SpanComponent.UI, SpanNames.GET_PKCE_DATA),
      resolve => {
        log('call getPKCEData');
        const traceContext = getTraceContext();
        window.impr?.authn.getPKCEData(
          traceContext.workflowId,
          traceContext,
          (codeChallenge: string, codeChallengeMethod: string) => {
            log('execute callback for getPKCEData');
            resolve({ codeChallenge, codeChallengeMethod });
          },
        );
      },
    ),
  );
}

/**
 * Retrieves Identity token and Coding context from the Endpoint Agent.
 * @returns Promise<IdentityTokenData> - the Identity Token Data
 */
export async function getIdentityToken(): Promise<IdentityTokenData> {
  return new Promise<IdentityTokenData>(
    instrument(
      createSpanName(SpanComponent.UI, SpanNames.GET_IDENTITY_TOKEN),
      resolve => {
        log('call getIdentityToken');
        const traceContext = getTraceContext();
        window.impr?.authn?.getIdentityToken(
          traceContext.workflowId,
          traceContext,
          (identityToken: string, codingContext: string) => {
            log('execute callback for getIdentityToken');
            resolve({ identityToken, codingContext });
          },
        );
      },
    ),
  );
}

/**
 * Retrieves information of Component from the Endpoint Agent.
 * @param componentId - The id of component to retrieve.
 * @returns Promise<ComponentData>
 */
export async function getComponentData(
  componentId: ComponentId,
): Promise<ComponentData> {
  return new Promise<ComponentData>(
    instrument(
      createSpanName(SpanComponent.UI, SpanNames.GET_COMPONENT_INFORMATION),
      resolve => {
        const traceContext = getTraceContext();
        window.impr?.update_manager?.getComponentInformation(
          traceContext.workflowId,
          traceContext,
          componentId,
          data => {
            log(
              'execute callback for getComponentInformation',
              'componentId:',
              componentId,
              ', data:',
              JSON.stringify(data),
            );
            resolve(data!);
          },
        );
      },
    ),
  );
}

/**
 * Retrieves Session Data from the Endpoint Agent.
 * @param keys - The keys of the session data to retrieve.
 * @returns Promise<SessionData> - the Session Data
 */
export async function getSessionData(
  keys: SessionDataKey[],
): Promise<SessionData> {
  return new Promise<SessionData>(
    instrument(
      createSpanName(SpanComponent.UI, SpanNames.GET_SESSION_DATA),
      resolve => {
        const traceContext = getTraceContext();
        log('call impr.session.getData', keys);
        window.impr?.session?.getData(
          traceContext.workflowId,
          traceContext,
          keys,
          (sessionData: SessionData) => {
            log('execute callback for impr.session.getData', keys);
            resolve(sessionData);
          },
        );
      },
    ),
  );
}

/**
 * Saves sessionData in the current user session.
 *
 * @param sessionData the session data
 */
export const setSessionData = instrument(
  createSpanName(SpanComponent.UI, SpanNames.SET_SESSION_DATA),
  (sessionData: SessionData): void => {
    const traceContext = getTraceContext();
    log('call impr.session.setData');
    window.impr?.session?.setData(
      traceContext.workflowId,
      traceContext,
      sessionData,
    );
  },
);

/**
 * Retrieves Session Data from the Endpoint Agent and clears the data.
 * @param keys - The keys of the session data to retrieve.
 * @returns Promise<SessionData> - the Session Data
 */
export async function getAndClearSessionData(
  keys: SessionDataKey[],
): Promise<SessionData> {
  return getSessionData(keys).then(result => {
    log('call impr.session.setData for clearing session data');
    // TODO VN-20977: change to setSessionData(null) or setSessionData([])
    setSessionData(keys.map(key => ({ [key]: '' })));
    return result;
  });
}
